import { useMemo } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { SelectSearch, SelectSearchBase } from "@telluscompany/design-system";

import { useAdminComplexesQuery } from "../../features/complexes/api/complexes";

interface BaseProps {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
  notFoundText?: string;
  /** Limit on options fetched. Defaults to 100. */
  limit?: number;
}

interface ControlledProps<TFieldValues extends FieldValues>
  extends BaseProps {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  value?: never;
  onChange?: never;
}

interface UncontrolledProps extends BaseProps {
  control?: never;
  name?: never;
  value: string | "" | undefined;
  onChange: (value: string) => void;
}

type ComplexComboboxProps<TFieldValues extends FieldValues = FieldValues> =
  | ControlledProps<TFieldValues>
  | UncontrolledProps;

/**
 * Combobox compartilhado para selecionar um complexo administrativo do tenant.
 *
 * Aceita dois modos exclusivos:
 * - Controlado por react-hook-form (`control` + `name`) — usa `SelectSearch`.
 * - Controlado externamente por estado (`value` + `onChange`) — usa
 *   `SelectSearchBase` diretamente, sem `useForm` interno, para evitar
 *   conflito entre o estado interno do form e o estado externo do consumidor
 *   (que provoca loop de re-render quando o pai reseta o valor).
 */
export function ComplexCombobox<TFieldValues extends FieldValues = FieldValues>(
  props: ComplexComboboxProps<TFieldValues>,
) {
  const {
    label,
    placeholder,
    disabled,
    className,
    clearable,
    notFoundText,
    limit = 100,
  } = props;

  const { data, isLoading } = useAdminComplexesQuery({ page: 1, limit });

  const options = useMemo(
    () =>
      data?.data?.map((complex) => ({
        label: complex.name,
        value: complex.id,
      })) ?? [],
    [data],
  );

  if ("control" in props && props.control && props.name) {
    return (
      <SelectSearch
        control={props.control}
        name={props.name}
        label={label}
        placeholder={placeholder}
        options={options}
        disabled={disabled || isLoading}
        className={className}
        clearable={clearable}
        notFoundText={notFoundText}
      />
    );
  }

  const { value, onChange } = props as UncontrolledProps;
  const isDisabled = disabled || isLoading;

  return (
    <div className={`grid gap-1 ${className ?? ""}`.trim()}>
      <label className={isDisabled ? "text-text-disabled" : undefined}>
        {label}
      </label>
      <SelectSearchBase
        value={value ?? ""}
        onChange={(next) => onChange(typeof next === "string" ? next : "")}
        placeholder={placeholder}
        options={options}
        disabled={isDisabled}
        clearable={clearable}
        notFoundText={notFoundText}
      />
    </div>
  );
}
